import React from 'react';
import { Helmet } from 'react-helmet';
import { FaCalculator, FaMotorcycle, FaMoneyCheck, FaUserTie, FaHandHoldingUsd, FaPercentage, FaDollarSign, FaTag, FaChartLine, FaPaypal } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import BikeEMICalculator from '../../components/calcualtors/IndianFinance/BikeEMICalculator';
import MarkupMarginComparison from '../../components/calcualtors/Sales/MarkupMarginComparison';

const FinancialCalculators = () => {
  return (
    <div className="min-h-screen bg-gradient-to-r from-blue-100 to-purple-100">
      <Helmet>
        <title>Financial Calculators | Comprehensive Financial Tools</title>
        <meta name="description" content="Collection of financial calculators for compound interest, loans, investments, and more. Make informed financial decisions with our easy-to-use tools." />
        <meta name="keywords" content="financial calculators, compound interest calculator, loan calculator, investment calculator, financial tools" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <FaCalculator className="header-icon text-4xl text-blue-600 mb-4" />
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Financial Calculators</h1>
          <p className="calculator-count text-xl text-gray-600">30+ calculators</p>
        </div>

        <div className="max-w-2xl mx-auto mb-12">
          <div className="calculator-description bg-white p-6 rounded-lg shadow-md">
            <p className="text-gray-600 mb-4">
              Welcome to the Financial Calculators section. Here you'll find a comprehensive
              collection of tools to help you with various financial calculations.
            </p>
            <button className="read-more inline-block bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors">
              Read more
            </button>
          </div>
        </div>

        <div className="calculator-section mb-12">
          <h2 className="section-title text-2xl font-bold text-gray-800 mb-6 flex items-center">
            Investment Calculators <span className="emoji ml-2">📊</span>
          </h2>
          <div className="calculator-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Link to="/financial/compound-interest" className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Compound Interest Calculator</h3>
              <p className="text-gray-600">Calculate compound interest and future value</p>
            </Link>
            <Link to="/financial/tds-interest" className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <h3 className="text-xl font-semibold text-gray-800 mb-2">TDS Interest Calculator</h3>
              <p className="text-gray-600">Calculate TDS interest on your investments</p>
            </Link>

            <Link to="/financial/roi" className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Return on Investment Calculator</h3>
              <p className="text-gray-600">Calculate the profitability of your investments.</p>
            </Link>

            <Link to="/financial/investment-portfolio" className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Investment Portfolio Calculator</h3>
              <p className="text-gray-600">Analyze your investment portfolio performance.</p>
            </Link>

            <Link to="/financial/stock" className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Stock Calculator</h3>
              <p className="text-gray-600">Calculate potential returns on stock investments.</p>
            </Link>

            <Link to="/financial/bond" className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Bond Calculator</h3>
              <p className="text-gray-600">Calculate bond prices, yields, and returns.</p>
            </Link>

            <Link to="/financial/rule-of-72" className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Rule of 72 Calculator</h3>
              <p className="text-gray-600">Estimate how long it will take for investments to double.</p>
            </Link>

            <Link to="/indian-finance/sip" className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <h3 className="text-xl font-semibold text-gray-800 mb-2">SIP Calculator</h3>
              <p className="text-gray-600">Calculate returns from your SIP investments.</p>
            </Link>
          </div>
        </div>

        <div className="calculator-section mb-12">
          <h2 className="section-title text-2xl font-bold text-gray-800 mb-6 flex items-center">
            Loan & Credit Calculators <span className="emoji ml-2">💰</span>
          </h2>
          <div className="calculator-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Link to="/financial/loan" className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Loan Calculator</h3>
              <p className="text-gray-600">Calculate loan payments, interest, and amortization schedules.</p>
            </Link>

            <Link to="/financial/mortgage" className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Mortgage Calculator</h3>
              <p className="text-gray-600">Calculate monthly mortgage payments and total interest.</p>
            </Link>

            <Link to="/financial/car-loan" className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Car Loan Calculator</h3>
              <p className="text-gray-600">Calculate monthly payments for car financing.</p>
            </Link>

            <Link to="/financial/credit-card" className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Credit Card Payoff Calculator</h3>
              <p className="text-gray-600">Determine how long it will take to pay off credit card debt.</p>
            </Link>

            <Link to="/financial/personal-loan" className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Personal Loan Calculator</h3>
              <p className="text-gray-600">Calculate repayments for personal loans.</p>
            </Link>

            <Link to="/financial/debt-consolidation" className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Debt Consolidation Calculator</h3>
              <p className="text-gray-600">Compare debt consolidation options to find the best solution.</p>
            </Link>

            <Link to="/finance/bike-emi" className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Bike EMI Calculator</h3>
              <p className="text-gray-600">Calculate your bike loan EMI and total cost</p>
            </Link>
          </div>
        </div>

        <div className="calculator-section mb-12">
          <h2 className="section-title text-2xl font-bold text-gray-800 mb-6 flex items-center">
            Savings & Retirement Calculators <span className="emoji ml-2">💸</span>
          </h2>
          <div className="calculator-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Link to="/financial/savings-goal" className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Savings Goal Calculator</h3>
              <p className="text-gray-600">Determine how much to save monthly to reach your financial goals.</p>
            </Link>

            <Link to="/financial/retirement" className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Retirement Calculator</h3>
              <p className="text-gray-600">Estimate how much you need to save for retirement.</p>
            </Link>

            <Link to="/financial/apy" className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <h3 className="text-xl font-semibold text-gray-800 mb-2">APY Calculator</h3>
              <p className="text-gray-600">Calculate annual percentage yield on savings accounts.</p>
            </Link>

            <Link to="/financial/education-savings" className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Education Savings Calculator</h3>
              <p className="text-gray-600">Plan for education expenses with this savings calculator.</p>
            </Link>

            <Link to="/financial/vacation-savings" className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Vacation Savings Calculator</h3>
              <p className="text-gray-600">Plan and save for your next vacation.</p>
            </Link>
          </div>
        </div>

        <div className="calculator-section mb-12">
          <h2 className="section-title text-2xl font-bold text-gray-800 mb-6 flex items-center">
            Tax & Wealth Calculators <span className="emoji ml-2">📊</span>
          </h2>
          <div className="calculator-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Link to="/financial/income-tax" className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Income Tax Calculator</h3>
              <p className="text-gray-600">Calculate your income tax liability and take-home pay.</p>
            </Link>

            <Link to="/financial/estate-tax" className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Estate Tax Calculator</h3>
              <p className="text-gray-600">Calculate potential estate tax liabilities.</p>
            </Link>

            <Link to="/financial/tax-bracket" className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Tax Bracket Calculator</h3>
              <p className="text-gray-600">Determine your tax bracket and effective tax rate.</p>
            </Link>

            <Link to="/financial/net-worth" className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Net Worth Calculator</h3>
              <p className="text-gray-600">Calculate your personal net worth and financial position.</p>
            </Link>

            <Link to="/financial/credit-score" className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Credit Score Estimator</h3>
              <p className="text-gray-600">Estimate your credit score based on financial factors.</p>
            </Link>
          </div>
        </div>

        <div className="calculator-section">
          <h2 className="section-title text-2xl font-bold text-gray-800 mb-6 flex items-center">
            Tax & Net Worth Calculators <span className="emoji ml-2">📝</span>
          </h2>
          <div className="calculator-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Link to="/financial/income-tax" className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Income Tax Calculator</h3>
              <p className="text-gray-600">Estimate your income tax liability.</p>
            </Link>

            <Link to="/financial/estate-tax" className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Estate Tax Calculator</h3>
              <p className="text-gray-600">Calculate potential estate tax liabilities.</p>
            </Link>

            <Link to="/financial/tax-bracket" className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Tax Bracket Calculator</h3>
              <p className="text-gray-600">Determine which tax bracket you fall into.</p>
            </Link>

            <Link to="/financial/net-worth" className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Net Worth Calculator</h3>
              <p className="text-gray-600">Calculate your personal net worth.</p>
            </Link>

            <Link to="/financial/credit-score" className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Credit Score Estimator</h3>
              <p className="text-gray-600">Estimate your credit score based on financial behavior.</p>
            </Link>
          </div><br/>

          <div className="calculator-section py-8">
            <h2 className="section-title text-2xl font-bold text-gray-800 mb-6 flex items-center justify-left">
              <span className="emoji mr-2 text-2xl">
                <i className="fas fa-calculator text-indigo-500"></i>
              </span>
              Indian Finance Calculators
            </h2>
            
            <div className="calculator-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <Link to="/indian-finance/atal-pension-yojana" className="calculator-card bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-all duration-300 hover:transform hover:scale-105">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-gray-800">Atal Pension Yojana Calculator</h3>
                  <i className="fas fa-piggy-bank text-indigo-500 text-xl"></i>
                </div>
                <p className="text-gray-600">Estimate your pension under the Atal Pension Yojana.</p>
              </Link>
      
              <Link to="/indian-finance/bike-emi" className="calculator-card bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-all duration-300 hover:transform hover:scale-105">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-gray-800">Bike EMI Calculator</h3>
                  <FaMotorcycle className="calculator-icon text-indigo-500 text-xl" />
                </div>
                <p className="text-gray-600">Calculate the EMI for your bike loan.</p>
              </Link>

              <Link to="/indian-finance/car-loan-emi" className="calculator-card bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-all duration-300 hover:transform hover:scale-105">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-gray-800">Car Loan EMI Calculator</h3>
                  <i className="fas fa-car text-indigo-500 text-xl"></i>
                </div>
                <p className="text-gray-600">Estimate the EMI for your car loan.</p>
              </Link>

              <Link to="/indian-finance/elss" className="calculator-card bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-all duration-300 hover:transform hover:scale-105">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-gray-800">ELSS Calculator</h3>
                  <i className="fas fa-chart-line text-indigo-500 text-xl"></i>
                </div>
                <p className="text-gray-600">Calculate potential returns from an ELSS investment.</p>
              </Link>

              <Link to="/indian-finance/emi" className="calculator-card bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-all duration-300 hover:transform hover:scale-105">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-gray-800">EMI Calculator</h3>
                  <i className="fas fa-calculator text-indigo-500 text-xl"></i>
                </div>
                <p className="text-gray-600">Calculate your monthly EMI for any loan.</p>
              </Link>

              <Link to="/indian-finance/epf" className="calculator-card bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-all duration-300 hover:transform hover:scale-105">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-gray-800">EPF Calculator</h3>
                  <i className="fas fa-piggy-bank text-indigo-500 text-xl"></i>
                </div>
                <p className="text-gray-600">Calculate your Employee Provident Fund returns.</p>
              </Link>

              <Link to="/indian-finance/home-loan-emi" className="calculator-card bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-all duration-300 hover:transform hover:scale-105">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-gray-800">Home Loan EMI Calculator</h3>
                  <i className="fas fa-home text-indigo-500 text-xl"></i>
                </div>
                <p className="text-gray-600">Estimate your EMI for a home loan.</p>
              </Link>

              <Link to="/indian-finance/hra-exemption" className="calculator-card bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-all duration-300 hover:transform hover:scale-105">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-gray-800">HRA Exemption Calculator</h3>
                  <i className="fas fa-file-alt text-indigo-500 text-xl"></i>
                </div>
                <p className="text-gray-600">Calculate your HRA exemptions based on salary and rent.</p>
              </Link>

              <Link to="/indian-finance/lumpsum" className="calculator-card bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-all duration-300 hover:transform hover:scale-105">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-gray-800">Lumpsum Calculator</h3>
                  <i className="fas fa-chart-line text-indigo-500 text-xl"></i>
                </div>
                <p className="text-gray-600">Calculate the returns from a lumpsum investment.</p>
              </Link>

              <Link to="/indian-finance/sip-lumpsum" className="calculator-card bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-all duration-300 hover:transform hover:scale-105">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-gray-800">SIP + Lumpsum Calculator</h3>
                  <i className="fas fa-chart-line text-indigo-500 text-xl"></i>
                </div>
                <p className="text-gray-600">Calculate returns for SIP and lumpsum investments combined.</p>
              </Link>

              <Link to="/indian-finance/moratorium" className="calculator-card bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-all duration-300 hover:transform hover:scale-105">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-gray-800">Moratorium Calculator</h3>
                  <i className="fas fa-clock text-indigo-500 text-xl"></i>
                </div>
                <p className="text-gray-600">Estimate the impact of a loan moratorium on your payments.</p>
              </Link>

              <Link to="/indian-finance/nps" className="calculator-card bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-all duration-300 hover:transform hover:scale-105">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-gray-800">NPS Calculator</h3>
                  <i className="fas fa-piggy-bank text-indigo-500 text-xl"></i>
                </div>
                <p className="text-gray-600">Estimate the returns from the National Pension Scheme.</p>
              </Link>

              <Link to="/indian-finance/personal-loan-emi" className="calculator-card bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-all duration-300 hover:transform hover:scale-105">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-gray-800">Personal Loan EMI Calculator</h3>
                  <i className="fas fa-calculator text-indigo-500 text-xl"></i>
                </div>
                <p className="text-gray-600">Calculate the EMI for your personal loan.</p>
              </Link>

              <Link to="/indian-finance/post-office-mis" className="calculator-card bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-all duration-300 hover:transform hover:scale-105">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-gray-800">Post Office MIS Calculator</h3>
                  <i className="fas fa-file-alt text-indigo-500 text-xl"></i>
                </div>
                <p className="text-gray-600">Calculate monthly income from Post Office MIS scheme.</p>
              </Link>

              <Link to="/indian-finance/ppf" className="calculator-card bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-all duration-300 hover:transform hover:scale-105">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-gray-800">PPF Calculator</h3>
                  <i className="fas fa-piggy-bank text-indigo-500 text-xl"></i>
                </div>
                <p className="text-gray-600">Estimate the returns on your PPF investment.</p>
              </Link>

              <Link to="/indian-finance/rd" className="calculator-card bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-all duration-300 hover:transform hover:scale-105">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-gray-800">RD Calculator</h3>
                  <i className="fas fa-chart-line text-indigo-500 text-xl"></i>
                </div>
                <p className="text-gray-600">Calculate the returns from a Recurring Deposit.</p>
              </Link>

              <Link to="/indian-finance/sukanya-samriddhi" className="calculator-card bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-all duration-300 hover:transform hover:scale-105">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-gray-800">Sukanya Samriddhi Yojana Calculator</h3>
                  <i className="fas fa-piggy-bank text-indigo-500 text-xl"></i>
                </div>
                <p className="text-gray-600">Estimate returns from the Sukanya Samriddhi Yojana scheme.</p>
              </Link>

              <Link to="/indian-finance/swp" className="calculator-card bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-all duration-300 hover:transform hover:scale-105">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-gray-800">SWP Calculator</h3>
                  <i className="fas fa-chart-line text-indigo-500 text-xl"></i>
                </div>
                <p className="text-gray-600">Estimate the income from your SWP investment.</p>
              </Link>

              <Link to="/indian-finance/sip" className="calculator-card bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-all duration-300 hover:transform hover:scale-105">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-gray-800">SIP Calculator</h3>
                  <i className="fas fa-chart-line text-indigo-500 text-xl"></i>
                </div>
                <p className="text-gray-600">Calculate returns from your SIP investments.</p>
              </Link>

              <Link to="/indian-finance/tds-interest" className="calculator-card bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-all duration-300 hover:transform hover:scale-105">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-gray-800">TDS Interest Calculator</h3>
                  <i className="fas fa-file-alt text-indigo-500 text-xl"></i>
                </div>
                <p className="text-gray-600">Calculate the interest on TDS deductions.</p>
              </Link>
            </div>
          </div>
          <div className="calculator-section">
      <h2 className="section-title text-2xl font-bold text-gray-800 mb-6 flex items-center">
        Sales Calculators 💸
        <span className="emoji ml-2 text-xl">
          <FaCalculator className="text-indigo-500" />
        </span>
      </h2>

      <div className="calculator-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <Link to="/sales/black-friday" className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
          <h3 className="text-xl font-semibold text-gray-800 mb-2 flex items-center">
            Black Friday Calculator
            <FaCalculator className="ml-2 text-green-500" />
          </h3>
          <p className="text-gray-600">Estimate your sales and discounts for Black Friday.</p>
        </Link>

        <Link to="/sales/cash-back" className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
          <h3 className="text-xl font-semibold text-gray-800 mb-2 flex items-center">
            Cash Back Calculator
            <FaMoneyCheck className="ml-2 text-green-500" />
          </h3>
          <p className="text-gray-600">Calculate the cash back you will receive on your purchases.</p>
        </Link>

        <Link to="/sales/check-digit" className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
          <h3 className="text-xl font-semibold text-gray-800 mb-2 flex items-center">
            Check Digit Calculator
            <FaCalculator className="ml-2 text-green-500" />
          </h3>
          <p className="text-gray-600">Validate numbers using a check digit formula.</p>
        </Link>

        <Link to="/sales/cltv" className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
          <h3 className="text-xl font-semibold text-gray-800 mb-2 flex items-center">
            CLTV Calculator
            <FaUserTie className="ml-2 text-green-500" />
          </h3>
          <p className="text-gray-600">Estimate the Customer Lifetime Value (CLTV) for your business.</p>
        </Link>

        <Link to="/sales/commission" className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
          <h3 className="text-xl font-semibold text-gray-800 mb-2 flex items-center">
            Commission Calculator
            <FaHandHoldingUsd className="ml-2 text-green-500" />
          </h3>
          <p className="text-gray-600">Calculate sales commission based on sales amount and percentage.</p>
        </Link>

        <Link to="/sales/cyber-monday" className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
          <h3 className="text-xl font-semibold text-gray-800 mb-2 flex items-center">
            Cyber Monday Calculator
            <FaCalculator className="ml-2 text-green-500" />
          </h3>
          <p className="text-gray-600">Estimate your sales and discounts for Cyber Monday.</p>
        </Link>

        <Link to="/sales/discount" className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
          <h3 className="text-xl font-semibold text-gray-800 mb-2 flex items-center">
            Discount Calculator
            <FaPercentage className="ml-2 text-green-500" />
          </h3>
          <p className="text-gray-600">Calculate the discount on a product based on the original price.</p>
        </Link>

        <Link to="/sales/double-discount" className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
          <h3 className="text-xl font-semibold text-gray-800 mb-2 flex items-center">
            Double Discount Calculator
            <FaPercentage className="ml-2 text-green-500" />
          </h3>
          <p className="text-gray-600">Calculate the total price after applying two successive discounts.</p>
        </Link>

        <Link to="/sales/margin" className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
          <h3 className="text-xl font-semibold text-gray-800 mb-2 flex items-center">
            Margin Calculator
            <FaDollarSign className="ml-2 text-green-500" />
          </h3>
          <p className="text-gray-600">Calculate your profit margin on a product.</p>
        </Link>

        <Link to="/sales/margin-tax" className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
          <h3 className="text-xl font-semibold text-gray-800 mb-2 flex items-center">
            Margin and Sales Tax Calculator
            <FaDollarSign className="ml-2 text-green-500" />
          </h3>
          <p className="text-gray-600">Calculate the profit margin and apply sales tax to the price.</p>
        </Link>

        <Link to="/sales/margin-vat" className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
          <h3 className="text-xl font-semibold text-gray-800 mb-2 flex items-center">
            Margin and VAT Calculator
            <FaDollarSign className="ml-2 text-green-500" />
          </h3>
          <p className="text-gray-600">Calculate the margin and VAT for a product.</p>
        </Link>

        <Link to="/sales/margin-classic" className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
          <h3 className="text-xl font-semibold text-gray-800 mb-2 flex items-center">
            Margin Calculator Classic
            <FaDollarSign className="ml-2 text-green-500" />
          </h3>
          <p className="text-gray-600">Classic method for calculating profit margin.</p>
        </Link>

        <Link to="/sales/margin-discount" className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
          <h3 className="text-xl font-semibold text-gray-800 mb-2 flex items-center">
            Margin With Discount Calculator
            <FaDollarSign className="ml-2 text-green-500" />
          </h3>
          <p className="text-gray-600">Calculate margin after applying a discount.</p>
        </Link>

        <Link to="/sales/markdown" className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
          <h3 className="text-xl font-semibold text-gray-800 mb-2 flex items-center">
            Markdown Calculator
            <FaTag className="ml-2 text-green-500" />
          </h3>
          <p className="text-gray-600">Calculate the price after a markdown on a product.</p>
        </Link>

        <Link to="/sales/markup" className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
          <h3 className="text-xl font-semibold text-gray-800 mb-2 flex items-center">
            Markup Calculator
            <FaChartLine className="ml-2 text-green-500" />
          </h3>
          <p className="text-gray-600">Calculate the markup price based on cost and desired profit.</p>
        </Link>

        <Link to="/sales/markup-margin" className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
          <h3 className="text-xl font-semibold text-gray-800 mb-2 flex items-center">
            Markup and Margin — Two Set Comparison Calculator
            <FaChartLine className="ml-2 text-green-500" />
          </h3>
          <p className="text-gray-600">Compare markup and margin on two sets of prices.</p>
        </Link>

        <Link to="/sales/markup-classic" className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
          <h3 className="text-xl font-semibold text-gray-800 mb-2 flex items-center">
            Markup Calculator Classic
            <FaChartLine className="ml-2 text-green-500" />
          </h3>
          <p className="text-gray-600">Classic markup calculation based on cost and desired price.</p>
        </Link>

        <Link to="/sales/paypal-fee" className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
          <h3 className="text-xl font-semibold text-gray-800 mb-2 flex items-center">
            PayPal Fee Calculator
            <FaPaypal className="ml-2 text-green-500" />
          </h3>
          <p className="text-gray-600">Calculate the fees PayPal will charge on transactions.</p>
        </Link>

        <Link to="/sales/percentage-discount" className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
          <h3 className="text-xl font-semibold text-gray-800 mb-2 flex items-center">
            Percentage Discount Calculator
            <FaPercentage className="ml-2 text-green-500" />
          </h3>
          <p className="text-gray-600">Calculate the discount percentage on a product.</p>
        </Link>

        <Link to="/sales/percent-off" className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
          <h3 className="text-xl font-semibold text-gray-800 mb-2 flex items-center">
            Percent Off Calculator
            <FaPercentage className="ml-2 text-green-500" />
          </h3>
          <p className="text-gray-600">Calculate how much you save with a percent-off discount.</p>
        </Link>

        <Link to="/sales/triple-discount" className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
          <h3 className="text-xl font-semibold text-gray-800 mb-2 flex items-center">
            Triple Discount Calculator
            <FaPercentage className="ml-2 text-green-500" />
          </h3>
          <p className="text-gray-600">Calculate the total price after applying three successive discounts.</p>
        </Link>

        <Link to="/sales/markup-margin-comparison" className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
          <h3 className="text-xl font-semibold text-gray-800 mb-2">Markup & Margin Comparison</h3>
          <p className="text-gray-600">Compare markup and margin between two sets of cost and selling prices</p>
        </Link>
      </div>
    </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialCalculators;